import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Inject
} from '@angular/core';
import { TermsAndConditionsService, TermsResult, TermsAcceptResult, AgreementInfo, AgreementAcceptance } from '@services/terms-and-conditions.service';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { toast } from '@web/util/toast';
import { observe } from '@web/util/loading/loading';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { tr } from '@util/tr';
import { isNumber } from 'lodash';

@Component({
  selector: 'app-terms-and-conditions',
  templateUrl: './terms-and-conditions.component.html',
  styleUrls: ['./terms-and-conditions.component.scss']
})
export class TermsAndConditionsComponent implements OnInit, OnDestroy {
  agreementsInfo: AgreementInfo[] = [];
  agreementsAcceptance: AgreementAcceptance[] = [];
  private destroy$ = new Subject<void>();
  isLoading: boolean = false;
  isLoadingAllTerms: boolean = false;
  hasTermsToAccept: boolean = false;
  isSubmitting: boolean = false;
  showAllTermsDialog: boolean = false;
  allTermsData: TermsResult | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: TermsResult,
    private dialogRef: MatDialogRef<TermsAndConditionsComponent>,
    private termsService: TermsAndConditionsService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.dialogRef.disableClose = true;
    this.agreementsInfo = this.data.agreementsInfoDto || [];
    this.hasTermsToAccept = this.agreementsInfo.length > 0;
    this.agreementsAcceptance = this.agreementsInfo.map(agreement => ({
      id: agreement.id,
      acceptWineSuiteUpdates: false,
      acceptExclusive: false
    }));
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  submitAcceptance(isAccepting: boolean) {
    if ((!this.canSubmit && isAccepting) || this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;

    if (!isAccepting) {
      this.uncheckAll();
    }

    observe(this.termsService.acceptTerms(this.agreementsAcceptance))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: TermsAcceptResult) => {
          this.isSubmitting = false;
          if (result.success) {
            toast(
              tr(
                isAccepting
                  ? 'Terms and conditions accepted successfully'
                  : 'Terms and conditions closed temporarily'
              )
            );
            this.closeClicked(isAccepting);
          } else {
            toast(
              tr(
                isAccepting
                  ? 'Error accepting terms. Please try again'
                  : 'Error closing temporarily. Please try again'
              )
            );
            this.closeClicked(false);
          }
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.isSubmitting = false;
          console.error('Error accepting terms:', error);
          toast(tr('Error accepting terms. Please try again'));
          this.closeClicked(false);
          this.cdr.detectChanges();
        }
      });
  }

  uncheckAll() {
    this.agreementsAcceptance.forEach(a => {
      a.acceptWineSuiteUpdates = false;
      a.acceptExclusive = false;
    });
  }

  get canSubmit(): boolean {
    return this.agreementsInfo.every(info => {
      const acceptance = this.getAcceptance(info.id);
      if (!acceptance) return false;
      if (info.acceptWineSuiteUpdates && !acceptance.acceptWineSuiteUpdates) return false;
      if (info.acceptExclusive && !acceptance.acceptExclusive) return false;
      return true;
    });
  }

  get hasMustAcceptTerms(): boolean {
    return this.agreementsInfo.some(a => a.mustAccept);
  }

  toggleAgreement(agreementId: string, type: 'acceptWineSuiteUpdates' | 'acceptExclusive') {
    const agreement = this.agreementsAcceptance.find(a => a.id === agreementId);
    if (agreement) {
      agreement[type] = !agreement[type];
    }
  }

  getAcceptance(agreementId: string): AgreementAcceptance | undefined {
    return this.agreementsAcceptance.find(a => a.id === agreementId);
  }

  closeClicked(accepted?: boolean) {
    this.dialogRef.close(accepted !== undefined ? { accepted } : {});
  }
}
